/**
 * 🚗 ADD VEHICLE FOR HOST DIALOG
 * Admin can add a new vehicle to a host's fleet
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  Typography,
  Box,
  FormControlLabel,
  Switch,
  InputAdornment,
  Divider,
} from '@mui/material';
import { DirectionsCar, AttachMoney, CloudUpload, Delete } from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import { adminVehicleService, CreateVehicleForHostRequest } from '@/services/adminVehicleService';
import { CURRENCY_CONFIG, type CurrencyCode } from '@/services/vehicleApprovalService';

interface AddVehicleForHostDialogProps {
  open: boolean;
  onClose: () => void;
  hostId: string;
  hostName: string;
  onSuccess?: () => void;
}

const NIGERIAN_STATES = [
  'Abia', 'Adamawa', 'Akwa Ibom', 'Anambra', 'Bauchi', 'Bayelsa', 'Benue', 'Borno', 'Cross River',
  'Delta', 'Ebonyi', 'Edo', 'Ekiti', 'Enugu', 'FCT', 'Gombe', 'Imo', 'Jigawa', 'Kaduna', 'Kano',
  'Katsina', 'Kebbi', 'Kogi', 'Kwara', 'Lagos', 'Nasarawa', 'Niger', 'Ogun', 'Ondo', 'Osun',
  'Oyo', 'Plateau', 'Rivers', 'Sokoto', 'Taraba', 'Yobe', 'Zamfara'
];

// Popular car makes in Nigeria
const CAR_MAKES = [
  'Acura', 'Audi', 'BMW', 'Chevrolet', 'Ford', 'GAC', 'Honda', 'Hyundai', 'Infiniti',
  'JAC', 'Kia', 'Land Rover', 'Lexus', 'Mazda', 'Mercedes-Benz', 'Mitsubishi', 'Nissan',
  'Peugeot', 'Pontiac', 'Porsche', 'Range Rover', 'Subaru', 'Toyota', 'Volkswagen', 'Volvo'
].sort();

// Popular models per make (top models only)
const CAR_MODELS: Record<string, string[]> = {
  'Toyota': ['Camry', 'Corolla', 'Highlander', 'RAV4', 'Sienna', 'Tacoma', 'Tundra', 'Venza', '4Runner', 'Avalon', 'Prius', 'Yaris'].sort(),
  'Honda': ['Accord', 'Civic', 'CR-V', 'Pilot', 'Odyssey', 'HR-V', 'Fit', 'Ridgeline'].sort(),
  'Lexus': ['ES', 'RX', 'GX', 'LX', 'IS', 'NX', 'UX', 'GS', 'LS'].sort(),
  'Mercedes-Benz': ['C-Class', 'E-Class', 'S-Class', 'GLE', 'GLC', 'GLA', 'GLB', 'GLS', 'A-Class', 'CLA', 'Sprinter'].sort(),
  'BMW': ['3 Series', '5 Series', '7 Series', 'X3', 'X5', 'X6', 'X7', 'X1', 'iX'].sort(),
  'Audi': ['A3', 'A4', 'A6', 'A8', 'Q3', 'Q5', 'Q7', 'Q8', 'e-tron'].sort(),
  'Ford': ['Explorer', 'Escape', 'Edge', 'Expedition', 'F-150', 'Ranger', 'Mustang', 'Focus', 'Fusion'].sort(),
  'Nissan': ['Altima', 'Maxima', 'Sentra', 'Rogue', 'Pathfinder', 'Murano', 'Armada', 'Frontier'].sort(),
  'Chevrolet': ['Malibu', 'Impala', 'Cruze', 'Equinox', 'Traverse', 'Tahoe', 'Suburban', 'Silverado'].sort(),
  'Hyundai': ['Elantra', 'Sonata', 'Tucson', 'Santa Fe', 'Palisade', 'Kona', 'Accent'].sort(),
  'Kia': ['Optima', 'Forte', 'Sportage', 'Sorento', 'Telluride', 'Soul', 'Rio', 'Stinger'].sort(),
  'Mazda': ['Mazda3', 'Mazda6', 'CX-3', 'CX-5', 'CX-9', 'CX-30', 'MX-5'].sort(),
  'Volkswagen': ['Jetta', 'Passat', 'Tiguan', 'Atlas', 'Golf', 'Arteon', 'ID.4'].sort(),
  'Subaru': ['Outback', 'Forester', 'Crosstrek', 'Ascent', 'Impreza', 'Legacy', 'WRX'].sort(),
  'Land Rover': ['Range Rover', 'Range Rover Sport', 'Range Rover Evoque', 'Discovery', 'Defender'].sort(),
  'Range Rover': ['Range Rover', 'Range Rover Sport', 'Range Rover Evoque', 'Range Rover Velar'].sort(),
  'Infiniti': ['Q50', 'Q60', 'QX50', 'QX60', 'QX80'].sort(),
  'Acura': ['TLX', 'MDX', 'RDX', 'ILX', 'NSX'].sort(),
  'Porsche': ['911', 'Cayenne', 'Macan', 'Panamera', 'Taycan'].sort(),
  'Volvo': ['S60', 'S90', 'XC40', 'XC60', 'XC90'].sort(),
  'Mitsubishi': ['Outlander', 'Eclipse Cross', 'Mirage', 'Pajero'].sort(),
  'Peugeot': ['206', '207', '307', '308', '407', '508', '2008', '3008', '5008'].sort(),
  'Pontiac': ['Grand Prix', 'G6', 'Vibe', 'Solstice'].sort(),
  'JAC': ['JS2', 'JS3', 'JS4', 'JS6', 'JS8', 'M8', 'Refine M5', 'Refine RF8', 'T8', 'T9', 'Refine', 'Ytterby'].sort(),
  'GAC': ['M8', 'GS3', 'GS4', 'GS5', 'GS8', 'Trumpchi'].sort(),
};

export const AddVehicleForHostDialog: React.FC<AddVehicleForHostDialogProps> = ({
  open,
  onClose,
  hostId,
  hostName,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [uploadedPhotos, setUploadedPhotos] = useState<{ imageData: string; category: string; fileName: string }[]>([]);
  const [formData, setFormData] = useState({
    // Vehicle Details
    make: '',
    model: '',
    year: new Date().getFullYear(),
    plateNumber: '',
    vin: '',
    color: '',
    transmission: 'Automatic' as 'Automatic' | 'Manual',
    fuelType: 'Petrol' as 'Petrol' | 'Diesel' | 'Electric' | 'Hybrid',
    seatingCapacity: 5,
    type: 'sedan' as 'sedan' | 'suv' | 'truck' | 'van' | 'coupe' | 'convertible',
    state: '',
    city: '',
    description: '',
    features: '',
    currentMarketValue: '',

    // Pricing
    selfDrive: '',
    withDriver: '',
    halfDaySelfDrive: '',
    halfDayWithDriver: '',
    fuelPriceFullTank: '',
    currency: 'NGN' as CurrencyCode,

    // Insurance
    insuranceType: 'Comprehensive',
    paymentTerm: 'Annual',
    hasGPS: true,
    hasInspectionCertificate: false,

    // Availability
    instantBooking: true,
  });

  const handlePhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    if (uploadedPhotos.length + files.length > 10) {
      toast.error('Maximum 10 photos allowed');
      return;
    }

    Array.from(files).forEach((file) => {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Max size is 10MB`);
        return;
      }

      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image file`);
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const imageData = e.target?.result as string;
        setUploadedPhotos(prev => [...prev, {
          imageData,
          category: 'general',
          fileName: file.name
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleRemovePhoto = (index: number) => {
    setUploadedPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.make || !formData.model || !formData.year || !formData.plateNumber || !formData.color) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!formData.selfDrive && !formData.withDriver) {
      toast.error('Please set at least one pricing option (Self-Drive or With Driver)');
      return;
    }

    setLoading(true);

    try {
      const requestData: CreateVehicleForHostRequest = {
        hostId,
        vehicle: {
          make: formData.make,
          model: formData.model,
          year: formData.year,
          plateNumber: formData.plateNumber,
          vin: formData.vin || undefined,
          color: formData.color,
          transmission: formData.transmission,
          fuelType: formData.fuelType,
          seatingCapacity: formData.seatingCapacity,
          type: formData.type,
          state: formData.state || undefined,
          city: formData.city || undefined,
          description: formData.description || undefined,
          features: formData.features ? formData.features.split(',').map(f => f.trim()) : undefined,
          currentMarketValue: formData.currentMarketValue ? parseFloat(formData.currentMarketValue) : undefined,
        },
        pricing: {
          selfDrive: formData.selfDrive ? parseFloat(formData.selfDrive) : null,
          withDriver: formData.withDriver ? parseFloat(formData.withDriver) : null,
          halfDaySelfDrive: formData.halfDaySelfDrive ? parseFloat(formData.halfDaySelfDrive) : null,
          halfDayWithDriver: formData.halfDayWithDriver ? parseFloat(formData.halfDayWithDriver) : null,
          fuelPriceFullTank: formData.fuelPriceFullTank ? parseFloat(formData.fuelPriceFullTank) : null,
          currency: formData.currency,
        },
        insurance: {
          type: formData.insuranceType,
          paymentTerm: formData.paymentTerm,
          hasGPS: formData.hasGPS,
          hasInspectionCertificate: formData.hasInspectionCertificate,
        },
        availability: {
          instantBooking: formData.instantBooking,
        }
      };

      const result = await adminVehicleService.createVehicleForHost(requestData);

      if (result.success) {
        const vehicleId = result.data?.vehicleId;

        // Upload photos if any were selected
        if (vehicleId && uploadedPhotos.length > 0) {
          toast.loading(`Uploading ${uploadedPhotos.length} photo${uploadedPhotos.length > 1 ? 's' : ''}...`);

          // Small delay to ensure database replication completes
          await new Promise(resolve => setTimeout(resolve, 1000));

          const photoResult = await adminVehicleService.uploadVehiclePhotos(vehicleId, uploadedPhotos);

          if (photoResult.success) {
            toast.dismiss();
            toast.success(`Vehicle and ${uploadedPhotos.length} photo${uploadedPhotos.length > 1 ? 's' : ''} added successfully!`);
          } else {
            toast.dismiss();
            toast.error(`Vehicle created but photo upload failed: ${photoResult.error}`);
          }
        } else {
          toast.success(`Vehicle added successfully to ${hostName}'s fleet!`);
        }

        onClose();
        if (onSuccess) onSuccess();
      } else {
        toast.error(result.error || 'Failed to create vehicle');
      }
    } catch (error) {
      console.error('Error creating vehicle:', error);
      toast.error('An error occurred while creating the vehicle');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DirectionsCar />
          <Typography variant="h6">Add Vehicle for {hostName}</Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Vehicle Information Section */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
              Vehicle Information
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              select
              label="Make"
              value={formData.make}
              onChange={(e) => setFormData({ ...formData, make: e.target.value, model: '' })}
              helperText="Select vehicle manufacturer"
            >
              {CAR_MAKES.map((make) => (
                <MenuItem key={make} value={make}>
                  {make}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              select
              label="Model"
              value={formData.model}
              onChange={(e) => setFormData({ ...formData, model: e.target.value })}
              disabled={!formData.make}
              helperText={!formData.make ? 'Select make first' : 'Select vehicle model'}
            >
              {formData.make && CAR_MODELS[formData.make] ? (
                CAR_MODELS[formData.make].map((model) => (
                  <MenuItem key={model} value={model}>
                    {model}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>
                  {formData.make ? 'No models available' : 'Select make first'}
                </MenuItem>
              )}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              type="number"
              label="Year"
              value={formData.year}
              onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
              inputProps={{ min: 1900, max: new Date().getFullYear() + 1 }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Plate Number"
              value={formData.plateNumber}
              onChange={(e) => setFormData({ ...formData, plateNumber: e.target.value.toUpperCase() })}
              placeholder="e.g., ABC-123"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="VIN (Optional)"
              value={formData.vin}
              onChange={(e) => setFormData({ ...formData, vin: e.target.value.toUpperCase() })}
              placeholder="17-digit VIN"
              inputProps={{ maxLength: 17 }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Color"
              value={formData.color}
              onChange={(e) => setFormData({ ...formData, color: e.target.value })}
              placeholder="e.g., Black"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Transmission"
              value={formData.transmission}
              onChange={(e) => setFormData({ ...formData, transmission: e.target.value as 'Automatic' | 'Manual' })}
            >
              <MenuItem value="Automatic">Automatic</MenuItem>
              <MenuItem value="Manual">Manual</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Fuel Type"
              value={formData.fuelType}
              onChange={(e) => setFormData({ ...formData, fuelType: e.target.value as any })}
            >
              <MenuItem value="Petrol">Petrol</MenuItem>
              <MenuItem value="Diesel">Diesel</MenuItem>
              <MenuItem value="Electric">Electric</MenuItem>
              <MenuItem value="Hybrid">Hybrid</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Vehicle Type"
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
            >
              <MenuItem value="sedan">Sedan</MenuItem>
              <MenuItem value="suv">SUV</MenuItem>
              <MenuItem value="truck">Truck</MenuItem>
              <MenuItem value="van">Van</MenuItem>
              <MenuItem value="coupe">Coupe</MenuItem>
              <MenuItem value="convertible">Convertible</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Seating Capacity"
              value={formData.seatingCapacity}
              onChange={(e) => setFormData({ ...formData, seatingCapacity: parseInt(e.target.value) })}
              inputProps={{ min: 1, max: 50 }}
            />
          </Grid>

          {/* Location */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 2 }}>
              Location
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="State"
              value={formData.state}
              onChange={(e) => setFormData({ ...formData, state: e.target.value })}
            >
              <MenuItem value="">Select State</MenuItem>
              {NIGERIAN_STATES.map(state => (
                <MenuItem key={state} value={state}>{state}</MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="City"
              value={formData.city}
              onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              placeholder="e.g., Ikeja"
            />
          </Grid>

          {/* Pricing Section */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 2 }}>
              <AttachMoney sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
              Pricing
            </Typography>
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              select
              label="Currency"
              value={formData.currency}
              onChange={(e) => setFormData({ ...formData, currency: e.target.value as CurrencyCode })}
            >
              {Object.entries(CURRENCY_CONFIG).map(([code, config]) => (
                <MenuItem key={code} value={code}>
                  {config.symbol} {config.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="24hr Self-Drive Rate"
              value={formData.selfDrive}
              onChange={(e) => setFormData({ ...formData, selfDrive: e.target.value })}
              InputProps={{
                startAdornment: <InputAdornment position="start">{CURRENCY_CONFIG[formData.currency].symbol}</InputAdornment>
              }}
              placeholder="e.g., 50000"
              helperText="Full day rental rate"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="24hr With Driver Rate"
              value={formData.withDriver}
              onChange={(e) => setFormData({ ...formData, withDriver: e.target.value })}
              InputProps={{
                startAdornment: <InputAdornment position="start">{CURRENCY_CONFIG[formData.currency].symbol}</InputAdornment>
              }}
              placeholder="e.g., 65000"
              helperText="Full day rental with driver"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="12hr Self-Drive Rate (Optional)"
              value={formData.halfDaySelfDrive}
              onChange={(e) => setFormData({ ...formData, halfDaySelfDrive: e.target.value })}
              InputProps={{
                startAdornment: <InputAdornment position="start">{CURRENCY_CONFIG[formData.currency].symbol}</InputAdornment>
              }}
              placeholder="e.g., 30000"
              helperText="Half day (12 hours) rental rate"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="12hr With Driver Rate (Optional)"
              value={formData.halfDayWithDriver}
              onChange={(e) => setFormData({ ...formData, halfDayWithDriver: e.target.value })}
              InputProps={{
                startAdornment: <InputAdornment position="start">{CURRENCY_CONFIG[formData.currency].symbol}</InputAdornment>
              }}
              placeholder="e.g., 40000"
              helperText="Half day with driver"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Full Tank Fuel Price (Optional)"
              value={formData.fuelPriceFullTank}
              onChange={(e) => setFormData({ ...formData, fuelPriceFullTank: e.target.value })}
              InputProps={{
                startAdornment: <InputAdornment position="start">{CURRENCY_CONFIG[formData.currency].symbol}</InputAdornment>
              }}
              placeholder="e.g., 25000"
              helperText="Add-on: Full tank of fuel"
            />
          </Grid>

          {/* Insurance Section */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 2 }}>
              Insurance & Safety
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Insurance Type"
              value={formData.insuranceType}
              onChange={(e) => setFormData({ ...formData, insuranceType: e.target.value })}
              placeholder="e.g., Comprehensive"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Payment Term"
              value={formData.paymentTerm}
              onChange={(e) => setFormData({ ...formData, paymentTerm: e.target.value })}
            >
              <MenuItem value="Monthly">Monthly</MenuItem>
              <MenuItem value="Quarterly">Quarterly</MenuItem>
              <MenuItem value="Annual">Annual</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.hasGPS}
                  onChange={(e) => setFormData({ ...formData, hasGPS: e.target.checked })}
                />
              }
              label="Has GPS Tracking"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.hasInspectionCertificate}
                  onChange={(e) => setFormData({ ...formData, hasInspectionCertificate: e.target.checked })}
                />
              }
              label="Has Inspection Certificate"
            />
          </Grid>

          {/* Vehicle Photos */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 2 }}>
              <CloudUpload sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
              Vehicle Photos (Optional)
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUpload />}
              disabled={uploadedPhotos.length >= 10}
              fullWidth
            >
              Upload Photos ({uploadedPhotos.length}/10)
              <input
                type="file"
                hidden
                multiple
                accept="image/*"
                onChange={handlePhotoUpload}
              />
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Upload up to 10 vehicle photos (max 10MB each). Photos will be stored in Cloudinary.
            </Typography>
          </Grid>

          {uploadedPhotos.length > 0 && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {uploadedPhotos.map((photo, index) => (
                  <Box
                    key={index}
                    sx={{
                      position: 'relative',
                      width: 100,
                      height: 100,
                      borderRadius: 1,
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <img
                      src={photo.imageData}
                      alt={photo.fileName}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        backgroundColor: 'error.main',
                        borderRadius: '50%',
                        width: 24,
                        height: 24,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: 'error.dark' }
                      }}
                      onClick={() => handleRemovePhoto(index)}
                    >
                      <Delete sx={{ fontSize: 16, color: 'white' }} />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Grid>
          )}

          {/* Additional Details */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 2 }}>
              Additional Details (Optional)
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description of the vehicle..."
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Features (comma-separated)"
              value={formData.features}
              onChange={(e) => setFormData({ ...formData, features: e.target.value })}
              placeholder="e.g., Air Conditioning, Bluetooth, USB"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Market Value"
              value={formData.currentMarketValue}
              onChange={(e) => setFormData({ ...formData, currentMarketValue: e.target.value })}
              InputProps={{
                startAdornment: <InputAdornment position="start">{CURRENCY_CONFIG[formData.currency].symbol}</InputAdornment>
              }}
              placeholder="e.g., 5000000"
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.instantBooking}
                  onChange={(e) => setFormData({ ...formData, instantBooking: e.target.checked })}
                />
              }
              label="Enable Instant Booking"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'Creating...' : 'Add Vehicle'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
